import React, { useState } from "react";
import { ITreatment, ISubTreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import { TreatmentCard } from "./TreatmentCard"; // Import the refactored card
import { TreatmentFormModal } from "./TreatmentFormModal"; // Import the new modal
import { SubTreatmentFormModal } from "./SubTreatmentFormModal"; // Import the new modal

import AddIcon from "@mui/icons-material/Add";
import bookUrl from "../../../../api/bookUrl";
import { toast } from "react-toastify";

interface Props {
  treatments: ITreatment[];
  onTreatmentsChange: (treatments: ITreatment[]) => void;
}

export const Treatments: React.FC<Props> = ({
  treatments,
  onTreatmentsChange,
}) => {
  // Main Delete Confirmation
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  // State for Add/Edit Treatment Modal
  const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<ITreatment | null>(
    null
  );

  // State for Add/Edit Sub-Treatment Modal
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<ISubTreatment | null>(null);
  const [parentTreatmentId, setParentTreatmentId] = useState<string | null>(
    null
  );

  // --- Main Treatment Handlers ---
  const handleOpenAddTreatment = () => {
    setEditingTreatment(null);
    setIsTreatmentModalOpen(true);
  };

  const handleOpenEditTreatment = (treatment: ITreatment) => {
    setEditingTreatment(treatment);
    setIsTreatmentModalOpen(true);
  };

  const handleSaveTreatment = (treatmentData: ITreatment) => {
    console.log(treatmentData);

    if (editingTreatment) {
      // Edit Mode
      onTreatmentsChange(
        treatments.map((t) =>
          t.id === editingTreatment.id ? { ...t, ...treatmentData } : t
        )
      );
    } else {
      // Add Mode

      const newTreatment = {
        ...treatmentData,
        id: `t_${Date.now()}`,
        subTreatments: [],
      };
      onTreatmentsChange([...treatments, newTreatment]);
    }
    setIsTreatmentModalOpen(false);
  };

  const handleDeleteTreatment = () => {
    console.log("New Treatments state:");
    if (!deleteCandidate) return;

    bookUrl
      .patch("/bookingclinic/clinic/deleteBookTreatments", {
        treatmentId: deleteCandidate,
      })
      .then((res) => {
        if (res.data?.success) {
          onTreatmentsChange(treatments.filter((t) => t.id !== deleteCandidate));
          setDeleteCandidate(null);
          toast.success("Treatment deleted successfully!");
        } else {
          toast.error("Failed to delete treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(error?.response?.data?.message || "Failed to delete treatment");
      });
  };

  // --- Sub-Treatment Handlers ---
  const handleOpenAddSub = (parentId: string) => {
    setParentTreatmentId(parentId);
    setEditingSub(null);
    setIsSubModalOpen(true);
  };

  const handleOpenEditSub = (parentId: string, sub: ISubTreatment) => {
    setParentTreatmentId(parentId);
    setEditingSub(sub);
    setIsSubModalOpen(true);
  };

  const handleSaveSubTreatment = (subData: ISubTreatment) => {
    if (!parentTreatmentId) return;

    onTreatmentsChange(
      treatments.map((t) => {
        if (t.id !== parentTreatmentId) return t;

        let newSubTreatments: ISubTreatment[];
        if (editingSub) {
          // Edit Sub
          newSubTreatments = t.subTreatments.map((s) =>
            s.id === editingSub.id ? { ...s, ...subData } : s
          );
        } else {
          // Add Sub
          const newSub = { ...subData, id: `st_${Date.now()}` };
          newSubTreatments = [...t.subTreatments, newSub];
        }
        return { ...t, subTreatments: newSubTreatments };
      })
    );
    bookUrl
      .post("/bookingadmin/subtreatment/addSubTreatment", {
        treatmentId: parentTreatmentId,
        subTreatment: subData,
      })
      .then((res) => {
        if (res.data?.success) {
          setIsSubModalOpen(false);
          toast.success(editingSub ? "Sub-treatment updated successfully!" : "Sub-treatment added successfully!");
        } else {
          toast.error("Failed to save sub-treatment");
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(error?.response?.data?.message || "Failed to save sub-treatment");
      });
  };

  const handleDeleteSubTreatment = (parentId: string, subId: string) => {
    onTreatmentsChange(
      treatments.map((t) => {
        if (t.id !== parentId) return t;
        const newSubs = t.subTreatments.filter((s) => s.id !== subId);
        return { ...t, subTreatments: newSubs };
      })
    );
  };

  return (
    <section className="bg-white rounded-[8px] shadow-lg shadow-slate-200/50 border border-slate-100 p-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 pb-2.5 mb-3 border-b border-slate-200">
        <h2 className="text-xs font-bold text-primary mb-0">Treatments</h2>
        <button 
          className="flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
          onClick={handleOpenAddTreatment}
        >
          <AddIcon sx={{ fontSize: 16 }} />
          Add Treatment
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {treatments.map((t, i) => (
          <TreatmentCard
            key={i}
            treatment={t}
            onDelete={() => setDeleteCandidate(t.id)}
            onEdit={() => handleOpenEditTreatment(t)}
            onAddSub={() => handleOpenAddSub(t.id)}
            onEditSub={(sub) => handleOpenEditSub(t.id, sub)}
            onDeleteSub={(subId) => handleDeleteSubTreatment(t.id, subId)}
          />
        ))}
      </div>

      {/* --- Modals --- */}
      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Treatment"
          message="Are you sure you want to delete this treatment and all its sub-treatments?"
          onConfirm={handleDeleteTreatment}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}

      {isTreatmentModalOpen && (
        <TreatmentFormModal
          treatment={editingTreatment}
          onClose={() => setIsTreatmentModalOpen(false)}
          onSave={handleSaveTreatment}
        />
      )}

      {isSubModalOpen && (
        <SubTreatmentFormModal
          subTreatment={editingSub}
          onClose={() => setIsSubModalOpen(false)}
          onSave={handleSaveSubTreatment}
        />
      )}
    </section>
  );
};
